import axios from 'axios';
import type { AxiosError, AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import type { AuthTokens } from '@/types';
import { toAppError } from './errors';
import { isTenantAccessToken, stripTenantQueryParams } from './jwt';
import { emitSessionExpired, tokenStorage } from './tokenStorage';
import { CSRF_COOKIE, CSRF_HEADER, authorizationHeader, hasCsrfCookie, readCookie, shouldAttemptRefresh } from './authRefresh';

/**
 * API Gateway base URL. `VITE_API_URL` is the documented name; `VITE_API_BASE_URL`
 * is kept so existing `.env` files keep working.
 */
export const API_BASE_URL: string =
  import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || '/api/v1';

/** Endpoints that must never trigger the refresh flow (they *are* the flow). */
const AUTH_FREE_PATHS = [
  '/auth/login',
  '/auth/mfa/setup',
  '/auth/mfa/enable',
  '/auth/mfa/verify',
  '/auth/refresh',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/register-operatore',
  '/auth/verify-email',
  '/auth/resend-verification',
];

function isAuthFree(url: string | undefined): boolean {
  if (!url) return false;
  return AUTH_FREE_PATHS.some((path) => url.startsWith(path));
}

/**
 * Axios serialises arrays as `stato[]=a&stato[]=b` by default, which NestJS's
 * `ValidationPipe` does not accept without extra config. Repeated keys
 * (`stato=a&stato=b`) are the least surprising contract, and `undefined`/`null`
 * entries are dropped so callers can pass partially-filled filter objects.
 */
function serializeParams(params: Record<string, unknown>): string {
  const search = new URLSearchParams();

  const append = (key: string, value: unknown): void => {
    if (value === undefined || value === null || value === '') return;
    if (value instanceof Date) {
      search.append(key, value.toISOString());
      return;
    }
    search.append(key, String(value));
  };

  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      if (value.length === 0) return;
      value.forEach((entry) => append(key, entry));
      return;
    }
    append(key, value);
  });

  return search.toString();
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
  paramsSerializer: serializeParams,
  withCredentials: true,
});

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    if (typeof config.headers.delete === 'function') config.headers.delete('Content-Type');
    else delete (config.headers as Record<string, unknown>)['Content-Type'];
  }
  if (!isAuthFree(config.url)) {
    const token = tokenStorage.getAccessToken();
    const bearer = authorizationHeader(token);
    if (bearer) config.headers.Authorization = bearer;
    if (config.params && isTenantAccessToken(token)) {
      config.params = stripTenantQueryParams(config.params);
    }
  }
  return config;
});

export const refreshClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

let refreshInFlight: Promise<AuthTokens> | null = null;

async function refreshTokens(): Promise<AuthTokens> {
  const csrf = readCookie(CSRF_COOKIE);
  if (!csrf) throw new Error('missing csrf cookie');

  const { data } = await refreshClient.post<AuthTokens>(
    '/auth/refresh',
    {},
    { headers: { [CSRF_HEADER]: csrf } },
  );
  tokenStorage.set(data);
  return data;
}

function requestRefresh(): Promise<AuthTokens> {
  refreshInFlight ??= refreshTokens().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

interface RetriableConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<{ code?: string }>) => {
    const config = error.config as RetriableConfig | undefined;
    const status = error.response?.status ?? 0;
    const code = error.response?.data && typeof error.response.data === 'object' ? error.response.data.code : undefined;

    const canRetry =
      config !== undefined &&
      shouldAttemptRefresh({
        status,
        code,
        retried: Boolean(config._retried),
        authFree: isAuthFree(config.url),
        hasCsrfCookie: hasCsrfCookie(),
      });

    if (canRetry && config) {
      config._retried = true;
      try {
        const tokens = await requestRefresh();
        const bearer = authorizationHeader(tokens.accessToken);
        if (bearer) config.headers.Authorization = bearer;
        return apiClient.request(config);
      } catch {
        emitSessionExpired();
        return Promise.reject(toAppError(error));
      }
    }

    if (status === 401 && !isAuthFree(config?.url) && !config?._retried) {
      emitSessionExpired();
    }

    return Promise.reject(toAppError(error));
  },
);

export const http = {
  get: async <TResponse>(url: string, config?: AxiosRequestConfig): Promise<TResponse> => {
    const response = await apiClient.get<TResponse>(url, config);
    return response.data;
  },
  post: async <TResponse, TBody = unknown>(url: string, body?: TBody, config?: AxiosRequestConfig): Promise<TResponse> => {
    const response = await apiClient.post<TResponse>(url, body, config);
    return response.data;
  },
  patch: async <TResponse, TBody = unknown>(url: string, body?: TBody, config?: AxiosRequestConfig): Promise<TResponse> => {
    const response = await apiClient.patch<TResponse>(url, body, config);
    return response.data;
  },
  put: async <TResponse, TBody = unknown>(url: string, body?: TBody, config?: AxiosRequestConfig): Promise<TResponse> => {
    const response = await apiClient.put<TResponse>(url, body, config);
    return response.data;
  },
  delete: async <TResponse = void>(url: string, config?: AxiosRequestConfig): Promise<TResponse> => {
    const response = await apiClient.delete<TResponse>(url, config);
    return response.data;
  },
};

export async function getFreshAccessToken(): Promise<string | null> {
  if (!tokenStorage.isAccessTokenExpiring() && tokenStorage.getAccessToken()) {
    return tokenStorage.getAccessToken();
  }
  if (!tokenStorage.hasRefreshSession()) return tokenStorage.getAccessToken();
  try {
    const tokens = await requestRefresh();
    return tokens.accessToken;
  } catch {
    emitSessionExpired();
    return null;
  }
}
